/**
 * Calendar-Refresh Utility
 * ------------------------------------------------------
 * • Guarantees at most ONE global callback at any time
 * • Never re-registers on every re-render
 * • Guards against re-entrancy so a refresh cannot be
 *   triggered again while a previous refresh is still
 *   running (avoids the X-per-second loop you saw)
 * ------------------------------------------------------
 */

/* keep our own reference so we can tell if we’re the owner */
let currentRefreshFn = null;
/* re-entrancy guard */
let refreshInFlight = false;

/* -------------------------------------------------- */
/* ⇢ Register ONCE (call inside useEffect(() => {}, []))  */
export const setupCalendarRefresh = refreshFn => {
  if (typeof refreshFn !== 'function') return;

  /* only replace if different */
  if (refreshFn !== currentRefreshFn) {
    window.refreshCalendarData = refreshFn;
    currentRefreshFn = refreshFn;
  }
};

/* -------------------------------------------------- */
/* ⇢ Unregister (call in the cleanup of the same effect) */
export const cleanupCalendarRefresh = () => {
  if (window.refreshCalendarData === currentRefreshFn) {
    delete window.refreshCalendarData; // or = null
  }
  currentRefreshFn = null;
};

/* -------------------------------------------------- */
/* ⇢ Trigger from anywhere
 *   - returns true if a refresh started, false otherwise
 *   - blocks concurrent triggers until the current one
 *     resolves or finishes (promise or sync)            */
export const triggerCalendarRefresh = () => {
  const fn = window.refreshCalendarData;
  if (typeof fn !== 'function' || refreshInFlight) return false;

  refreshInFlight = true;
  try {
    /* if the callback returns a promise, wait for it   */
    const maybePromise = fn();
    if (maybePromise && typeof maybePromise.finally === 'function') {
      maybePromise.finally(() => (refreshInFlight = false));
    } else {
      /* sync callback → unlock immediately             */
      refreshInFlight = false;
    }
  } catch (err) {
    refreshInFlight = false;
    throw err; // surface it
  }
  return true;
};
